import { useState, useCallback } from 'react';
import { supabase } from '../services/supabaseService';

/**
 * Mapuje rekord z bazy na obiekt używany przez komponenty
 */
export const mapProperty = (p) => ({
  id: p.id,
  image_url: Array.isArray(p.image_url)
    ? p.image_url
    : typeof p.image_url === 'string' && p.image_url
    ? [p.image_url]
    : [],
  title: p.title || `${p.city || ''} ${p.district || ''}`.trim(),
  price: p.price || 0,
  priceString: p.price
    ? `${p.price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} zł/mies.`
    : '',
  area: p.area || '',
  rooms: p.rooms != null ? p.rooms : '',
  floor: p.floor || '',
  total_floors: p.total_floors || '',
  address: [p.street, p.building_number, p.city, p.district].filter(Boolean).join(', '),
  parking: p.amenities ? p.amenities.includes('Parking') : false,
  rent: p.rent || '',
  wynajem: p.price || '',
  city: p.city || '',
  street: p.street || '',
  building_number: p.building_number || '',
  district: p.district || '',
  amenities: p.amenities || [],
  equipment: p.equipment || [],
  description: p.description || '',
  deposit: p.deposit || '',
  available_from: p.available_from || '',
  contact: { name: p.first_name || '', phone: p.phone || '', email: p.email || '' },
  user_id: p.user_id || '',
  phone_reveals: p.phone_reveals || 0,
  views: p.views || 0,
  previous_price: p.previous_price || null,
  price_reduced_at: p.price_reduced_at || null,
});

export const useProperties = ({ setRecentProperties } = {}) => {
  const [userListings, setUserListings] = useState([]);
  const [reducedProperties, setReducedProperties] = useState([]);
  const [userTenantProfiles, setUserTenantProfiles] = useState([]);

  const fetchInitialProperties = useCallback(async () => {
    try {
      const { propertiesService } = await import('../services/supabaseService');
      const all = await propertiesService.getAll();
      if (all && Array.isArray(all) && all.length > 0) {
        const mapped = all.map(mapProperty);
        if (setRecentProperties) setRecentProperties(mapped);
      } else {
        if (setRecentProperties) setRecentProperties([]);
      }
    } catch (err) {
    }
  }, [setRecentProperties]);

  const fetchReducedProperties = useCallback(async () => {
    try {
      const now = new Date();
      const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000).toISOString();
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .not('previous_price', 'is', null)
        .not('price_reduced_at', 'is', null)
        .gt('price_reduced_at', fourteenDaysAgo)
        .order('price_reduced_at', { ascending: false });
      if (error) throw error;
      // Guard: tylko faktyczne obniżki (previous_price > price)
      const mapped = (data || [])
        .filter((p) => p.previous_price > p.price)
        .map((p) => ({
          ...mapProperty(p),
          previous_price: p.previous_price,
          price_reduced_at: p.price_reduced_at,
        }));
      setReducedProperties(mapped);
    } catch (err) {
    }
  }, []);

  const fetchUserListings = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('user_id', userId)
        .order('id', { ascending: false });
      if (error) throw error;
      setUserListings(data || []);
    } catch (err) {
      setUserListings([]);
    }
  }, []);

  const handleDeleteListing = useCallback(async (propertyId, userId, onSuccess) => {
    if (!userId) return;
    try {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId)
        .eq('user_id', userId);
      if (error) throw error;
      await fetchUserListings(userId);
      if (onSuccess) await onSuccess();
    } catch (err) {
      alert('Błąd podczas usuwania ogłoszenia: ' + err.message);
    }
  }, [fetchUserListings]);

  const fetchUserTenantProfiles = useCallback(async (userId) => {
    if (!userId) return;
    try {
      const { tenantsService } = await import('../services/supabaseService');
      const profiles = await tenantsService.getByUserId(userId);
      setUserTenantProfiles(profiles || []);
    } catch (err) {
      setUserTenantProfiles([]);
    }
  }, []);

  return {
    userListings,
    setUserListings,
    reducedProperties,
    setReducedProperties,
    fetchInitialProperties,
    fetchReducedProperties,
    fetchUserListings,
    handleDeleteListing,
    userTenantProfiles,
    setUserTenantProfiles,
    fetchUserTenantProfiles,
  };
};
